import { useState } from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment-timezone';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  CRAWLER_DAYS,
  CRAWLER_PERIOD_TYPES,
  CRAWLER_TYPES,
} from '../../../../constants/crawlerConstants';
import Text from '../../../elements/text/Text';
import TimezoneSelect from '../../../elements/timezone-select/TimezoneSelect';
import Modal from '../../../sections/modal/Modal';
import { filteredPeriods } from '../crawlerUtils';

const CrawlStep5 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { control, getValues, setValue } = useFormContext();
  const { errors } = useFormState({ control });

  const { periodType, periodTime, jobType } = getValues();

  const [modal, setModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    moment(periodTime || '00:00', 'HH:mm')
  );

  const toggleModal = () => setModal(!modal);
  const toProfilePage = () => navigate('/profile');
  const resetSchedule = () => {
    setValue('day', '');
    setValue('date', '');
  };

  return (
    <>
      <Box sx={{ my: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Text title>
            {t('crawler.scheduleSettings', { ns: 'sectionTitles' })}
          </Text>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid size={12}>
          <FormControl error={!!errors?.periodType}>
            <Controller
              name="periodType"
              control={control}
              defaultValue={CRAWLER_PERIOD_TYPES.MANUAL.value}
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  value={field.value}
                  row
                  onChange={(e) => {
                    resetSchedule();
                    field.onChange(e.target.value);
                  }}
                >
                  {filteredPeriods(CRAWLER_TYPES[jobType].SCHEDULE).map(
                    (type) => (
                      <FormControlLabel
                        key={type.value}
                        value={type.value}
                        control={<Radio />}
                        label={t(type.label, { ns: 'menus' })}
                      />
                    )
                  )}
                </RadioGroup>
              )}
            />
            <FormHelperText>{errors?.periodType?.message}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid container spacing={2} size={12}>
          {periodType !== CRAWLER_PERIOD_TYPES.MANUAL.value && (
            <>
              <Grid size={12}>
                <Text variant="body2">
                  {t('crawlerTimezone', { ns: 'descriptions' })}
                </Text>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                }}
              >
                <Controller
                  name="periodTime"
                  control={control}
                  render={({ field: { onChange } }) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <TimePicker
                        ampm={false}
                        minutesStep={10}
                        label={t('timePeriod', { ns: 'fields' })}
                        value={selectedDate}
                        onChange={(date) => {
                          setSelectedDate(date || '');
                          onChange(date ? moment(date).format('HH:mm') : '');
                        }}
                        views={['hours', 'minutes']}
                        slotProps={{
                          textField: {
                            error: !!errors.periodTime,
                            helperText: errors.periodTime
                              ? errors.periodTime.message
                              : null,
                            inputProps: { 'aria-label': 'time-input' },
                          },
                        }}
                        variant="outline"
                        size="small"
                      />
                    </LocalizationProvider>
                  )}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                }}
              >
                <TimezoneSelect
                  getValues={getValues}
                  errors={errors}
                  setValue={setValue}
                  variant="outlined"
                />
              </Grid>
            </>
          )}
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            {periodType === CRAWLER_PERIOD_TYPES.WEEKLY.value && (
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                error={!!errors?.day}
              >
                <InputLabel>{t('day', { ns: 'fields' })}</InputLabel>
                <Controller
                  name="day"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={t('day', { ns: 'fields' })}
                      inputProps={{ 'aria-label': 'day-input' }}
                    >
                      {CRAWLER_DAYS.map((day) => (
                        <MenuItem key={day.value} value={day.value}>
                          {t(day.label, { ns: 'menus' })}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors?.day?.message}</FormHelperText>
              </FormControl>
            )}
            {periodType === CRAWLER_PERIOD_TYPES.MONTHLY.value && (
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                error={!!errors?.date}
              >
                <InputLabel>{t('date', { ns: 'fields' })}</InputLabel>
                <Controller
                  name="date"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      label={t('date', { ns: 'fields' })}
                      inputProps={{ 'aria-label': 'date-input' }}
                    >
                      {[...Array(31).keys()].map((date) => (
                        <MenuItem key={date} value={date + 1}>
                          {date + 1}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors?.date?.message}</FormHelperText>
              </FormControl>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Modal
        openModal={modal}
        title={t('warning', { ns: 'general' })}
        showActions
        handleClose={toggleModal}
        handleConfirm={toProfilePage}
      >
        {t('loseAllData', { ns: 'descriptions' })}
      </Modal>
    </>
  );
};

export default CrawlStep5;
